import React, { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Loader2, Briefcase, MapPin, Upload, Check, Copy, Search, ChevronRight, Globe } from "lucide-react";
import { fmtD } from "../lib/date";
import { SmsConsent } from "../components/legal/SmsConsent";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const EMPTY = { name: "", phone: "", email: "", expectedSalary: "", availableDate: "", resumeText: "", smsConsent: false };
const T = {
  zh: {
    brand: "ALIGCUS 阿里建筑 · 招聘门户", sub: "Join Us · 在线投递简历，随时查询进度",
    trackPh: "粘贴进度查询码…", track: "查进度", openJobs: "在招职位", loading: "加载中…",
    empty: "当前暂无在招职位，欢迎稍后再来", apply: "立即投递", back: "← 返回职位列表",
    applyFor: "应聘：", hiring: (n) => `招 ${n} 人`, posted: "发布",
    name: "姓名 *", phone: "电话", email: "邮箱", salary: "期望薪资 ($)", date: "可到岗日期",
    resume: "简历（上传 PDF/TXT 或粘贴文本，二选一）", upload: "上传简历文件", chosen: "已选择：",
    paste: "或在此粘贴简历全文…", submit: "提交投递", submitFail: "投递失败，请重试",
    successTitle: "投递成功！我们已收到您的简历", successSub: "请保存以下进度查询链接，随时了解招聘进展：",
    copy: "复制", footer: "ALIGCUS Construction · 简历仅用于招聘评估，我们将妥善保管您的个人信息",
  },
  en: {
    brand: "ALIGCUS Construction · Careers", sub: "Join Us · Apply online and track your application anytime",
    trackPh: "Paste tracking code…", track: "Track", openJobs: "Open Positions", loading: "Loading…",
    empty: "No open positions right now — please check back soon", apply: "Apply Now", back: "← Back to positions",
    applyFor: "Applying for: ", hiring: (n) => `${n} opening${n > 1 ? "s" : ""}`, posted: "posted",
    name: "Full Name *", phone: "Phone", email: "Email", salary: "Expected Salary ($)", date: "Available Date",
    resume: "Resume (upload PDF/TXT or paste text)", upload: "Upload Resume File", chosen: "Selected: ",
    paste: "Or paste your resume here…", submit: "Submit Application", submitFail: "Submission failed, please retry",
    successTitle: "Application received!", successSub: "Save this tracking link to follow your application status:",
    copy: "Copy", footer: "ALIGCUS Construction · Your resume is used for recruiting only and kept confidential",
  },
};

export default function Careers() {
  const { jobId } = useParams();
  const [lang, setLang] = useState(localStorage.getItem("careers_lang") || "zh");
  const [jobs, setJobs] = useState(null);
  const [applyJob, setApplyJob] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [file, setFile] = useState(null);
  const fileRef = useRef(null);
  const [submitting, setSubmitting] = useState(false);
  const [doneToken, setDoneToken] = useState("");
  const [err, setErr] = useState("");
  const [track, setTrack] = useState("");
  const t = T[lang];

  const toggleLang = () => {
    const nl = lang === "zh" ? "en" : "zh";
    setLang(nl);
    localStorage.setItem("careers_lang", nl);
  };

  useEffect(() => {
    fetch(`${BACKEND}/api/careers/jobs`).then((r) => r.json()).then(setJobs).catch(() => setJobs([]));
  }, []);
  useEffect(() => {
    if (jobId && jobs) {
      const j = jobs.find((x) => x.id === jobId);
      if (j) { setApplyJob(j); setForm(EMPTY); setFile(null); }
    }
  }, [jobId, jobs]);
  useEffect(() => {
    if (applyJob && !sessionStorage.getItem(`cv_${applyJob.id}`)) {
      sessionStorage.setItem(`cv_${applyJob.id}`, "1");
      fetch(`${BACKEND}/api/careers/jobs/${applyJob.id}/view`, { method: "POST" }).catch(() => {});
    }
  }, [applyJob]);

  const submit = async () => {
    setErr(""); setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("jobId", applyJob.id);
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      if (file) fd.append("file", file);
      const res = await fetch(`${BACKEND}/api/careers/apply`, { method: "POST", body: fd });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || t.submitFail);
      setDoneToken(d.token);
    } catch (e) { setErr(e.message || t.submitFail); }
    setSubmitting(false);
  };
  const copyTrack = async () => {
    const url = `${window.location.origin}/careers/status/${doneToken}`;
    try { await navigator.clipboard.writeText(url); } catch { /* noop */ }
  };
  const inp = "mt-1 w-full text-sm rounded-lg border border-slate-200 px-3 py-2 focus:outline-none focus:ring-1 focus:ring-brand-400";
  const jBranch = (j) => (lang === "zh" ? j.branch : j.branchEn || j.branch);
  const jRole = (j) => (lang === "zh" ? j.roleZh : j.roleEn || j.roleZh);
  const jDesc = (j) => j.description || (lang === "zh" ? (j.resp || []) : (j.respEn || j.resp || [])).join(lang === "zh" ? "；" : "; ");

  return (
    <div className="min-h-screen bg-slate-100" data-testid="careers-page">
      <div className="bg-slate-900 text-white px-5 py-5">
        <div className="max-w-[860px] mx-auto flex flex-wrap items-center gap-3">
          <div>
            <p className="font-extrabold text-lg">{t.brand}</p>
            <p className="text-[12px] text-slate-300">{t.sub}</p>
          </div>
          <div className="ml-auto flex items-center gap-1.5 flex-wrap">
            <button data-testid="careers-lang-toggle" onClick={toggleLang}
              className="inline-flex items-center gap-1 rounded-full border border-slate-600 text-slate-200 hover:border-slate-400 text-xs font-semibold px-3 py-1.5">
              <Globe className="h-3 w-3" />{lang === "zh" ? "EN" : "中文"}
            </button>
            <input data-testid="careers-track-input" value={track} onChange={(e) => setTrack(e.target.value)}
              placeholder={t.trackPh} className="rounded-full bg-slate-800 border border-slate-700 text-xs text-white px-3 py-1.5 w-44 focus:outline-none" />
            <Link data-testid="careers-track-go" to={`/careers/status/${(track.split("/").pop() || "").trim()}`}
              className="inline-flex items-center gap-1 rounded-full bg-brand-500 hover:bg-brand-600 text-white text-xs font-semibold px-3 py-1.5"><Search className="h-3 w-3" />{t.track}</Link>
          </div>
        </div>
      </div>

      <div className="max-w-[860px] mx-auto p-4">
        {doneToken ? (
          <div className="bg-white rounded-2xl border border-green-200 p-8 text-center my-6" data-testid="careers-success">
            <Check className="h-12 w-12 text-green-500 mx-auto mb-3" />
            <p className="text-lg font-bold text-slate-800">{t.successTitle}</p>
            <p className="text-sm text-slate-500 mt-1">{t.successSub}</p>
            <div className="mt-3 flex items-center justify-center gap-2 flex-wrap">
              <Link data-testid="careers-track-link" to={`/careers/status/${doneToken}`}
                className="text-sm text-indigo-600 underline break-all">{window.location.origin}/careers/status/{doneToken}</Link>
              <button data-testid="careers-track-copy" onClick={copyTrack}
                className="inline-flex items-center gap-1 rounded-full border border-slate-300 text-slate-500 text-xs font-semibold px-3 py-1"><Copy className="h-3 w-3" />{t.copy}</button>
            </div>
          </div>
        ) : applyJob ? (
          <div className="bg-white rounded-2xl border border-slate-200 p-5 my-4" data-testid="careers-form">
            <button onClick={() => { setApplyJob(null); setErr(""); }} className="text-[12px] text-slate-400 hover:text-slate-600 mb-2">{t.back}</button>
            <p className="text-base font-bold text-slate-800">{t.applyFor}<span className="text-red-600">{applyJob.title}</span></p>
            <p className="text-[12px] text-slate-400 mb-3">{jBranch(applyJob)} · {t.hiring(applyJob.openings)}</p>
            <div className="grid sm:grid-cols-2 gap-3 text-xs text-slate-500">
              <label>{t.name}<input data-testid="careers-name" className={inp} value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} /></label>
              <label>{t.phone}<input data-testid="careers-phone" className={inp} value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} /></label>
              <label>{t.email}<input data-testid="careers-email" className={inp} value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} /></label>
              <label>{t.salary}<input data-testid="careers-salary" className={inp} value={form.expectedSalary} onChange={(e) => setForm((f) => ({ ...f, expectedSalary: e.target.value }))} /></label>
              <label>{fmtD(t.date)}<input data-testid="careers-date" type="date" className={inp} value={form.availableDate} onChange={(e) => setForm((f) => ({ ...f, availableDate: e.target.value }))} /></label>
            </div>
            <div className="mt-3">
              <p className="text-xs text-slate-500 mb-1">{t.resume}</p>
              <input ref={fileRef} type="file" accept=".pdf,.txt" className="hidden" data-testid="careers-resume-file"
                onChange={(e) => setFile(e.target.files?.[0] || null)} />
              <button type="button" data-testid="careers-resume-upload-btn" onClick={() => fileRef.current?.click()}
                className="inline-flex items-center gap-1.5 rounded-full border border-indigo-300 text-indigo-600 hover:bg-indigo-50 text-xs font-semibold px-4 py-2">
                <Upload className="h-3.5 w-3.5" />{file ? `${t.chosen}${file.name}` : t.upload}
              </button>
              <textarea data-testid="careers-resume-text" rows={4} className={inp + " mt-2"} value={form.resumeText}
                onChange={(e) => setForm((f) => ({ ...f, resumeText: e.target.value }))} placeholder={t.paste} />
            </div>
            <div className="mt-3 rounded-lg border border-slate-100 bg-slate-50 px-3 py-2"><SmsConsent value={form.smsConsent} onChange={(v) => setForm((f) => ({ ...f, smsConsent: v }))} testid="careers-sms-consent" compact /></div>
            {err && <p data-testid="careers-error" className="mt-3 text-[12px] text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{err}</p>}
            <button data-testid="careers-submit" disabled={submitting || !form.name.trim() || (!file && !form.resumeText.trim())} onClick={submit}
              className="mt-4 w-full h-12 rounded-xl bg-brand-500 hover:bg-brand-600 disabled:opacity-40 text-white font-semibold flex items-center justify-center gap-2">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}{t.submit}
            </button>
          </div>
        ) : (
          <div className="space-y-3 my-4">
            <p className="text-sm font-bold text-slate-600">{t.openJobs} {jobs ? `(${jobs.length})` : ""}</p>
            {jobs === null ? (
              <div className="text-center py-12 text-slate-400"><Loader2 className="h-5 w-5 animate-spin inline" /></div>
            ) : jobs.length === 0 ? (
              <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center text-slate-400 text-sm" data-testid="careers-empty">{t.empty}</div>
            ) : jobs.map((j) => (
              <div key={j.id} className="bg-white rounded-2xl border border-slate-200 p-4 flex flex-wrap items-center gap-3" data-testid={`careers-job-${j.id}`}>
                <div className="h-10 w-10 rounded-xl bg-red-50 flex items-center justify-center shrink-0"><Briefcase className="h-5 w-5 text-red-500" /></div>
                <div className="flex-1 min-w-[200px]">
                  <p className="font-bold text-slate-800">{j.title}<span className="text-[11px] text-slate-400 font-normal ml-2">{jRole(j)}</span></p>
                  <p className="text-[12px] text-slate-400 mt-0.5 flex items-center gap-1"><MapPin className="h-3 w-3" />{jBranch(j)} · {t.hiring(j.openings)} · {j.postedAt} {t.posted}</p>
                  {jDesc(j) && <p className="text-[12px] text-slate-500 mt-1 line-clamp-2">{jDesc(j)}</p>}
                </div>
                <button data-testid={`careers-apply-btn-${j.id}`} onClick={() => { setApplyJob(j); setForm(EMPTY); setFile(null); }}
                  className="inline-flex items-center gap-1 rounded-full bg-slate-900 hover:bg-slate-700 text-white text-xs font-semibold px-4 py-2">{t.apply}<ChevronRight className="h-3.5 w-3.5" /></button>
              </div>
            ))}
          </div>
        )}
        <p className="text-[11px] text-slate-400 text-center pb-6">{t.footer}</p>
      </div>
    </div>
  );
}
